import { env } from "./env";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

/**
 * Loads Google's gtag.js global site tag and configures it for our GA4 property, once. A no-op
 * entirely when `VITE_GA4_MEASUREMENT_ID` isn't set — the property doesn't exist yet, or this is
 * local dev — so this is always safe to call unconditionally at startup. Standard boilerplate from
 * GA4's own "Install the Google tag" setup flow, adapted to TypeScript. The base tag automatically
 * fires a page_view on this initial load; it does not additionally re-fire on a client-side SPA
 * route change (see trackPageView below).
 */
export function initGa4(): void {
  if (initialized || !env.VITE_GA4_MEASUREMENT_ID) return;
  initialized = true;

  const measurementId = env.VITE_GA4_MEASUREMENT_ID;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag("js", new Date());
  window.gtag("config", measurementId);
}

/**
 * Fires a page_view for a client-side route change — the base tag's own automatic pageview-on-load
 * only covers the very first load, not subsequent SPA navigations (same reasoning as
 * trackPageView in xPixel.ts). Call this on every route change (see AppShell.tsx). A no-op when
 * `VITE_GA4_MEASUREMENT_ID` isn't configured.
 */
export function trackPageView(path: string): void {
  if (!env.VITE_GA4_MEASUREMENT_ID || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
